import { isObject, replaceObject } from '../utils/util_helpers';
import 'moment';
import moment from 'moment-timezone';
import * as RNLocalize from 'react-native-localize';
import { getNextEventInstance } from '../utils/util_date_checker';

import DATA from '../configs/config_data';

// region Convert Error
export function convertError(errorObj) {
    if (isObject(errorObj)) {
        if (errorObj.hasOwnProperty('headers')) {
            return('Status Code: ' + errorObj.status);
        }
        else {
            // Extract error data
            let errorStr = '';
            let errorArr = Object.keys(errorObj);
            errorArr.forEach((errorKey) => {
                errorStr += (errorArr.length > 1 ? '- ' : '') + (errorObj[errorKey])[0].toString();

                // Add line break
                if (errorArr.length > 1) {
                    errorStr += '\n';
                }
            });

            return errorStr;
        }
    }
    else if (typeof errorObj === 'string') {
        return(errorObj);
    }
    else return errorObj?.toString();

}
// endregion

// region Function - Check if error exist in API response
export function checkErrorExist(responseObj) {
    if (!isObject(responseObj) && !Array.isArray(responseObj)) return true;

    let errorFields = ['errors', 'error', 'exception'],
        errorExists = errorFields.some((field) => responseObj.hasOwnProperty(field));

    return(errorExists);
}
// endregion

export function getConvertedTimezone(timezone) {
    let isUTCOffset = (timezone?.startsWith('+') || timezone?.startsWith('-'));

    const newTimezone = replaceObject(timezone || '', DATA.replaceTimezone);
    return(isUTCOffset ? timezone : (newTimezone ? (DATA.timezoneData.find((item) => item.toLowerCase() === newTimezone.toLowerCase()) ? newTimezone : 'America/Denver') : 'America/Denver'));
}

// region Convert To Moment With Timezone
export function toTimezoneMoment(dateString, timezone = 'America/Denver') {
    let isUTCOffset = (timezone?.startsWith('+') || timezone?.startsWith('-'));
    return(isUTCOffset ? moment(dateString, 'YYYY-MM-DD HH:mm:ss').utcOffset(timezone) : moment.tz(dateString, timezone));
}
// endregion

export function toCurrentDeviceTime(dateString, timezone = 'America/Denver') {
    return(toTimezoneMoment(dateString, timezone).tz(RNLocalize.getTimeZone()));
}

// region Calculate Distance between 2 dates
export function dateDistance(date1, date2) {
    let timeDiff = Math.abs(date2.getTime() - date1.getTime());
    let diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return(diffDays);
}
// endregion

// region Check Date
export function checkDate(calendarObj, ignoreTimezone = false) {
    const timeArr = checkDateArray(calendarObj, ignoreTimezone, true);
    return(timeArr[0] || {});
}
// endregion

// region Function - Check Date Array
export function checkDateArray(calendarObj, ignoreTimezone = null, isCheckDateSingle = false, cutOffDateUnix = null) {
    // Convert timezone - Moment objects
    const deviceTimezone = RNLocalize.getTimeZone();
    const convertedTimezone = getConvertedTimezone(calendarObj['timezone']);

    const start =
        ignoreTimezone ?
            (
                ignoreTimezone === 'UTC' ?
                    moment.utc(calendarObj.start_time)
                    :
                    moment(calendarObj.start_time, 'YYYY-MM-DD HH:mm:ss')
            )
            :
            toTimezoneMoment(calendarObj.start_time, convertedTimezone).tz(deviceTimezone);

    const end =
        ignoreTimezone ?
            (
                ignoreTimezone === 'UTC' ?
                    moment.utc(calendarObj.end_time)
                    :
                    moment(calendarObj.end_time, 'YYYY-MM-DD HH:mm:ss')
            )
            :
            toTimezoneMoment(calendarObj.end_time, convertedTimezone).tz(deviceTimezone);

    // Unix unit
    const currentUnix = moment().tz(deviceTimezone).unix();

    // Result data in unix
    let currentStartUnix = start.unix(), currentEndUnix = end.unix(),
        lastStartUnix = start.unix(), lastEndUnix = end.unix();

    // Get the repeat time
    const extendedRepeatTimes = 30; // To use with infinitely repeating events
    const repeatTimes = calendarObj['times_repeated'] || 365; // Default to fetch at most (case of infinite daily event) 1 year time ahead
    const isInfinity = !calendarObj['times_repeated'];
    const interval = calendarObj.interval ? ((calendarObj.interval === 0) ? 1 : calendarObj.interval) : 1;

    // Check for recurring
    const freq = calendarObj.frequency ? (isObject(calendarObj.frequency) ? calendarObj.frequency.name.toLowerCase() : calendarObj.frequency.toLowerCase()) : 'once';
    const momentSymbol = (freq === 'weekly' ? 'w' : (freq === 'monthly' ? 'M' : 'd')).toString();

    // Check if event is happening in the future comparing to current time
    let isNoMore = false;

    const createTimeObj = (onGoing = false) => {
        const timeDifferenceStart = currentStartUnix - currentUnix;

        const dateStr = moment.unix(currentStartUnix).utc().format('YYYY-MM-DD');

        if ((calendarObj['calendar_exceptions'] || []).find((item) => item.date === dateStr)) {
            return null;
        }

        return({
            startTime: currentStartUnix,
            endTime: currentEndUnix,

            lastStartTime: lastStartUnix,
            lastEndTime: lastEndUnix,

            utcDateStr: dateStr,

            onGoing,

            isNoMore, // Last event instance
            freq,

            isOver: currentUnix > currentEndUnix,

            in24h: ((timeDifferenceStart >= 0) && (timeDifferenceStart <= 86400)),
            in15h: ((timeDifferenceStart >= 0) && (timeDifferenceStart <= 54000)),
            isSameDay: moment.unix(currentStartUnix).isSame(moment.unix(currentEndUnix), 'day'),
            timeStr:
                currentStartUnix >= currentUnix ?
                    'Starting ' + moment.unix(currentStartUnix).fromNow()
                    :
                    (
                        currentEndUnix >= currentUnix ?
                            'Ending '
                            :
                            'Ended '
                    )
                    + moment.unix(currentEndUnix).fromNow()
        });
    };

    // Array
    let tempArr = [];

    switch(freq) {
        case 'once':
        {
            const newTimeObj = createTimeObj(currentUnix >= currentStartUnix);
            (newTimeObj) && tempArr.push(newTimeObj);
            break;
        }
        default:
        {
            // Check if this event still have any instance left
            const nextTimeObj = getNextEventInstance(calendarObj);
            if (!nextTimeObj) return([]);
            if (isCheckDateSingle) return([nextTimeObj]);

            // If valid, start with that time object
            const countStart = moment.unix(nextTimeObj.startTime);
            const countEnd = moment.unix(nextTimeObj.endTime);


            const stoppingPoint = (repeatTimes + (isInfinity ? extendedRepeatTimes : 1));
            for (let i = (isInfinity ? 0 : nextTimeObj.diffCount); i <= stoppingPoint; ++i) {
                currentStartUnix = countStart.unix();
                currentEndUnix = countEnd.unix();

                // Push to array - only future events are added
                if (
                    (cutOffDateUnix ? (currentEndUnix <= cutOffDateUnix) : true) &&
                    ((currentEndUnix > currentUnix) || (i === stoppingPoint))
                ) {
                    if (i === stoppingPoint) isNoMore = true;
                    const newTimeObj = createTimeObj((currentStartUnix <= currentUnix) && (currentUnix <= currentEndUnix));
                    (newTimeObj) && tempArr.push(newTimeObj);
                }

                // Keep counting
                countStart.add(interval, momentSymbol);
                countEnd.add(interval, momentSymbol);
            }
        }
    }

    // Print out results
    return(tempArr);
}
// endregion
